<template>
  <div>
    <head>
      <title>Theatre Schedule | County Hospital</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>
      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Header -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Theatre Schedule</h4>
                  <button class="btn btn-sm btn-primary" @click="$router.push('schedule-procedure')">
                    <i class="bx bx-plus me-1"></i> Schedule Procedure
                  </button>
                </div>
              </div>
            </div>

            <!-- Filters -->
            <div class="row g-2 mb-3 align-items-end">
              <div class="col-md-3">
                <label class="form-label small mb-1">From</label>
                <input type="date" class="form-control form-control-sm" v-model="filters.startDate">
              </div>
              <div class="col-md-3">
                <label class="form-label small mb-1">To</label>
                <input type="date" class="form-control form-control-sm" v-model="filters.endDate">
              </div>
              <div class="col-md-3">
                <label class="form-label small mb-1">Theatre Room</label>
                <select class="form-select form-select-sm" v-model="filters.theatreRoom">
                  <option value="">All Rooms</option>
                  <option v-for="r in rooms" :key="r.theatreRoomID || r.roomName" :value="r.roomName">
                    {{ r.roomName }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <button class="btn btn-primary btn-sm w-100" @click="load">
                  <i class="bx bx-filter me-1"></i> Apply
                </button>
              </div>
            </div>

            <!-- KPI strip -->
            <div class="row g-2 mb-3">
              <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-muted">Bookings</small><h5 class="mb-0">{{ schedule.length }}</h5></div></div>
              <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-warning">Today</small><h5 class="mb-0 text-warning">{{ counts.today }}</h5></div></div>
              <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-info">Scheduled</small><h5 class="mb-0 text-info">{{ counts.scheduled }}</h5></div></div>
              <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-success">Completed</small><h5 class="mb-0 text-success">{{ counts.completed }}</h5></div></div>
            </div>

            <!-- States -->
            <div v-if="loading" class="text-center text-muted py-4">
              <span class="spinner-border spinner-border-sm me-1"></span>Loading…
            </div>
            <div v-else-if="!schedule.length" class="text-center text-muted py-4">
              No theatre bookings in this range.
            </div>

            <!-- Day-grouped list -->
            <div v-else>
              <div v-for="(group, day) in groupedByDay" :key="'day-'+day" class="mb-4">
                <h6 class="text-uppercase text-muted mb-2">
                  <i class="bx bx-calendar me-1"></i> {{ formatHeading(day) }}
                  <span class="badge bg-light text-dark ms-2">{{ group.length }} booking<span v-if="group.length !== 1">s</span></span>
                </h6>

                <!-- Phone cards -->
                <div class="d-md-none">
                  <div v-for="s in group" :key="'card-'+s.surgeryRequestID"
                       class="card border mb-2 shadow-sm" style="cursor:pointer;">
                    <div class="card-body p-3">
                      <div class="d-flex justify-content-between align-items-start mb-1">
                        <strong>{{ s.formattedTime || s.scheduledTime }} · {{ s.theatreRoom || 'No room' }}</strong>
                        <span class="badge" :class="statusBadge(s.status)">{{ s.status }}</span>
                      </div>
                      <div>{{ s.surgeryName || s.procedureName }}</div>
                      <div class="text-muted small">
                        {{ s.patientName }} <span v-if="s.patientNo"> · #{{ s.patientNo }}</span>
                      </div>
                      <div class="text-muted small">
                        Surgeon: {{ s.surgeonName || s.performingDoctorName || '—' }}
                      </div>
                      <span class="badge mt-1" :class="priorityBadge(s.priority)">{{ s.priority || 'ROUTINE' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Tablet/desktop table -->
                <div class="table-responsive d-none d-md-block">
                  <table class="table table-sm table-hover align-middle border">
                    <thead class="table-light">
                    <tr>
                      <th style="width:90px;">Time</th>
                      <th>Room</th>
                      <th>Patient</th>
                      <th>Procedure</th>
                      <th>Surgeon</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="s in group" :key="'row-'+s.surgeryRequestID">
                      <td><strong>{{ s.formattedTime || s.scheduledTime }}</strong></td>
                      <td>{{ s.theatreRoom || '—' }}</td>
                      <td><strong>{{ s.patientName }}</strong> <small class="text-muted" v-if="s.patientNo">#{{ s.patientNo }}</small></td>
                      <td>{{ s.surgeryName || s.procedureName }}</td>
                      <td>{{ s.surgeonName || s.performingDoctorName || '—' }}</td>
                      <td><span class="badge" :class="priorityBadge(s.priority)">{{ s.priority || 'ROUTINE' }}</span></td>
                      <td><span class="badge" :class="statusBadge(s.status)">{{ s.status }}</span></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { execute, hospital, getRight } from "@/api";

export default {
  name: "TheatreSchedule",
  components: { NavigationBar },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    const oneWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    return {
      loading: false,
      schedule: [],
      rooms: [],
      filters: { startDate: today, endDate: oneWeek, theatreRoom: '' }
    };
  },
  computed: {
    groupedByDay() {
      const map = {};
      for (const s of this.schedule) {
        const d = (s.scheduledDate || '').slice(0, 10);
        if (!map[d]) map[d] = [];
        map[d].push(s);
      }
      const sortedKeys = Object.keys(map).sort();
      const out = {};
      for (const k of sortedKeys) out[k] = map[k];
      return out;
    },
    counts() {
      const today = new Date().toISOString().slice(0, 10);
      let t = 0, sc = 0, cm = 0;
      for (const s of this.schedule) {
        if ((s.scheduledDate || '').slice(0, 10) === today) t++;
        const st = (s.status || '').toUpperCase();
        if (st === 'SCHEDULED' || st === 'PENDING') sc++;
        if (st === 'COMPLETED' || st === 'DONE')     cm++;
      }
      return { today: t, scheduled: sc, completed: cm };
    }
  },
  mounted() { this.load(); },
  methods: {
    getRight(t) { return getRight(t); },
    load() {
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getTheatreSchedule');
      data.append('facilityID', sessionStorage.getItem('facilityID') || '');
      data.append('startDate', this.filters.startDate);
      data.append('endDate',   this.filters.endDate);
      if (this.filters.theatreRoom) data.append('theatreRoom', this.filters.theatreRoom);
      execute(data, hospital).then(res => {
        this.loading = false;
        if (res.data && res.data.success) {
          this.schedule = res.data.data.schedule || [];
          this.rooms    = res.data.data.rooms    || [];
        } else {
          this.schedule = []; this.rooms = [];
        }
      }).catch(() => {
        this.loading = false; this.schedule = []; this.rooms = [];
      });
    },
    formatHeading(d) {
      if (!d) return '—';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      const today = new Date(); today.setHours(0,0,0,0);
      const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
      if (dt.toDateString() === today.toDateString())    return 'Today · ' + dt.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
      if (dt.toDateString() === tomorrow.toDateString()) return 'Tomorrow · ' + dt.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
      return dt.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'short', year:'numeric' });
    },
    statusBadge(s) {
      const u = (s || '').toUpperCase();
      if (u === 'SCHEDULED' || u === 'PENDING') return 'bg-info';
      if (u === 'IN_PROGRESS' || u === 'STARTED') return 'bg-primary';
      if (u === 'COMPLETED' || u === 'DONE') return 'bg-success';
      if (u === 'POSTPONED') return 'bg-warning text-dark';
      if (u === 'CANCELLED') return 'bg-secondary';
      return 'bg-light text-dark';
    },
    priorityBadge(p) {
      if (p === 'EMERGENCY') return 'bg-danger';
      if (p === 'URGENT')    return 'bg-warning text-dark';
      return 'bg-secondary';
    }
  }
};
</script>
